import { useEffect, useRef, useState } from "react"

interface TimeRangeSliderProps {
  className?: string
  onChange: (startTime: string, endTime: string) => void
  startTime: string
  endTime: string
}

function TimeRangeSlider({
  className,
  onChange,
  startTime,
  endTime,
}: TimeRangeSliderProps) {
  const [isDraggingStart, setIsDraggingStart] = useState(false)
  const [isDraggingEnd, setIsDraggingEnd] = useState(false)
  const trackRef = useRef<HTMLDivElement>(null)

  // Convert time string (HH:MM) to minutes since midnight
  const timeToMinutes = (time: string): number => {
    // console.log("time", time);
    const [hours, minutes] = time?.split(":")?.map(Number)
    return hours * 60 + minutes
  }

  // Convert minutes since midnight to time string (HH:MM)
  const minutesToTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`
  }

  // Convert position to time
  const positionToTime = (position: number): string => {
    if (!trackRef.current) return "00:00"

    const trackWidth = trackRef.current.offsetWidth
    const trackRect = trackRef.current.getBoundingClientRect()
    const relativePosition = Math.max(0, Math.min(position - trackRect.left, trackWidth))

    // Calculate percentage of track width
    const percentage = relativePosition / trackWidth

    // Convert to minutes (24 hours = 1440 minutes)
    const totalMinutes = Math.round((percentage * 1440) / 30) * 30

    return minutesToTime(totalMinutes)
  }

  // Calculate position percentage based on time
  const timeToPercentage = (time: string): number => {
    const minutes = timeToMinutes(time)
    return (minutes / 1440) * 100
  }

  // Format time for display (12-hour format with AM/PM)
  const formatTimeForDisplay = (time: string): string => {
    const [hours, minutes] = time?.split(":")?.map(Number)
    const period = hours >= 12 ? "PM" : "AM"
    const displayHours = hours % 12 || 12
    return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`
  }

  // Handle mouse/touch move events
  const handleMove = (clientX: number) => {
    if (!isDraggingStart && !isDraggingEnd) return

    const newTime = positionToTime(clientX)
    const newMinutes = timeToMinutes(newTime)

    if (isDraggingStart) {
      const endMinutes = timeToMinutes(endTime)
      if (newMinutes < endMinutes) {
        onChange(newTime, endTime)
      }
    } else if (isDraggingEnd) {
      const startMinutes = timeToMinutes(startTime)
      if (newMinutes > startMinutes) {
        onChange(startTime, newTime)
      }
    }
  }

  // Mouse event handlers
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => handleMove(e.clientX)

    const handleMouseUp = () => {
      setIsDraggingStart(false)
      setIsDraggingEnd(false)
    }

    if (isDraggingStart || isDraggingEnd) {
      document.addEventListener("mousemove", handleMouseMove)
      document.addEventListener("mouseup", handleMouseUp)
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
    }
  }, [isDraggingStart, isDraggingEnd])

  // Touch event handlers
  useEffect(() => {
    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches && e.touches[0]) {
        handleMove(e.touches[0].clientX)
      }
    }

    const handleTouchEnd = () => {
      setIsDraggingStart(false)
      setIsDraggingEnd(false)
    }

    if (isDraggingStart || isDraggingEnd) {
      document.addEventListener("touchmove", handleTouchMove)
      document.addEventListener("touchend", handleTouchEnd)
    }

    return () => {
      document.removeEventListener("touchmove", handleTouchMove)
      document.removeEventListener("touchend", handleTouchEnd)
    }
  }, [isDraggingStart, isDraggingEnd])

  // Notify parent component of changes
  useEffect(() => {
    onChange?.(startTime, endTime)
  }, [startTime, endTime, onChange])

  return (
    <div className="w-full space-y-4">
      <div className="flex justify-between text-sm ">
        <span>12 AM</span>
        <span>6 AM</span>
        <span>12 PM</span>
        <span>6 PM</span>
        <span>12 AM</span>
      </div>

      <div ref={trackRef} className="relative h-2 w-full rounded-full bg-gray-200">
        {/* Selected range */}
        <div
          className="absolute h-full rounded-full bg-blue-700"
          style={{
            left: `${timeToPercentage(startTime)}%`,
            width: `${timeToPercentage(endTime) - timeToPercentage(startTime)}%`,
          }}
        />

        {/* Start handle */}
        <div
          className="absolute top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-full border-2 border-blue-600 bg-white shadow-md active:cursor-grabbing"
          style={{ left: `${timeToPercentage(startTime)}%` }}
          onMouseDown={() => setIsDraggingStart(true)}
          onTouchStart={() => setIsDraggingStart(true)}
          role="slider"
          aria-label="Start time"
          aria-valuetext={formatTimeForDisplay(startTime)}
          tabIndex={0}
        />

        {/* End handle */}
        <div
          className="absolute top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-full border-2 border-blue-600 bg-white shadow-md active:cursor-grabbing"
          style={{ left: `${timeToPercentage(endTime)}%` }}
          onMouseDown={() => setIsDraggingEnd(true)}
          onTouchStart={() => setIsDraggingEnd(true)}
          role="slider"
          aria-label="End time"
          aria-valuetext={formatTimeForDisplay(endTime)}
          tabIndex={0}
        />
      </div>

      <div className="flex justify-between">
        <div className="text-sm font-medium">Start: {formatTimeForDisplay(startTime)}</div>
        <div className="text-sm font-medium">End: {formatTimeForDisplay(endTime)}</div>
      </div>
    </div>
  )
}

export default TimeRangeSlider;
